import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Building2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const field = "w-full border border-border px-3 py-2 text-sm bg-background focus:outline-none focus:border-[#002FA7]";

// Admin: create a school credit pool (only AFTER the bank transfer is received) and track usage.
export function AdminOrgPools() {
  const [pools, setPools] = useState([]);
  const [form, setForm] = useState({ name: "", credits_total: 500, per_teacher: 50 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/org-pools").then(({ data }) => setPools(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Le virement de ${form.name} est-il bien reçu ? ${form.credits_total} crédits vont être créés.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/org-pools", {
        name: form.name, credits_total: Number(form.credits_total), per_teacher: Number(form.per_teacher),
      });
      toast.success(`Réserve créée. Code à transmettre : ${data.code}`, { duration: 15000 });
      setForm({ ...form, name: "" });
      load();
    } catch (err) {
      toast("Création impossible. Vérifiez les champs.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <section className="border border-border p-6 max-w-3xl mt-6" data-testid="admin-org-pools">
      <div className="sux-marker mb-2 flex items-center gap-2"><Building2 className="w-3 h-3" /> Établissements<InfoTip testid="admin-org-pools-info" content={"Une réserve = un stock de crédits pour une école, avec un code. Chaque enseignant saisit le code dans « Recharger » et reçoit sa part, jusqu'à épuisement du stock."} /></div>
      <h3 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "Outfit" }}>Réserves de crédits</h3>
      <p className="text-xs text-muted-foreground mb-4">À créer uniquement après réception du virement (devis à 0,077 € le crédit, minimum 500 crédits).</p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Nom (ex. Collège Jean Moulin)"
          className={`${field} md:col-span-2`} data-testid="admin-org-name-input" />
        <input type="number" min={1} required value={form.credits_total} onChange={set("credits_total")} title="Crédits au total"
          className={field} data-testid="admin-org-total-input" />
        <input type="number" min={1} required value={form.per_teacher} onChange={set("per_teacher")} title="Crédits par enseignant"
          className={field} data-testid="admin-org-per-teacher-input" />
        <button type="submit" disabled={busy} data-testid="admin-org-create-btn"
          className="md:col-span-4 font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 flex justify-center">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Créer la réserve (total · par enseignant)"}
        </button>
      </form>
      <div className="mt-6 divide-y divide-border" data-testid="admin-org-list">
        {pools.length === 0 && <p className="text-sm text-muted-foreground">Aucun établissement pour l'instant.</p>}
        {pools.map((p) => (
          <div key={p.id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`admin-org-row-${p.code}`}>
            <span className="font-semibold flex-1 min-w-[160px]">{p.name}</span>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(p.code); toast("Code copié."); }}
              className="font-mono text-[#002FA7] flex items-center gap-1" data-testid={`admin-org-copy-${p.code}`}>
              {p.code} <Copy className="w-3 h-3" />
            </button>
            <span className="font-mono text-xs">{p.credits_left}/{p.credits_total} crédits</span>
            <span className="font-mono text-xs">{p.teachers} prof(s) · {p.per_teacher}/prof</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default AdminOrgPools;
